import { useMemo } from "react";
import { PaymentHistory } from "@/types/payment-history.type";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { UserProfileBadge } from "@/modules/finance/components/transaction/columns/UserProfileBadge";
import { ZoneNameBadge } from "@/modules/finance/components/transaction/columns/ZoneNameBadge";
import { StatusBadge } from "@/modules/finance/components/transaction/columns/StatusBadge";
import { TransactionState } from "@/modules/finance/components/transaction/columns/TransactionState";
import { moneyFormat } from "@/helper/format-data";
import { SortIcon } from "@/components/data-table/SortIcon";
import { toLocalString } from "@/helper/date/to-local-string.util";
import { defaultDurationOptions } from "@/helper/date/constants/duration.const";
import { numberToDateTimeString } from "@/helper/date/number-to-date-time.util";
import { formatDuration } from "@/helper/date/format-duration.util";
import { PaymentMethodLogo } from "@/modules/finance/components/transaction/columns/PaymentMethodLogo";

export const usePaymentHistoryColumn = () => {
  return useMemo<ColumnDef<PaymentHistory>[]>(
    () => [
      {
        accessorKey: "start_date",
        id: "start_date",
        header: ({ column }) => {
          return (
            <Button
              className="text-left py-3 px-2 font-semibold text-table-header"
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            >
              Date de début
              <SortIcon column={column} />
            </Button>
          );
        },
        cell: ({ row }) => {
          const { date, time } = toLocalString({
            date: row.original.start_date,
            locale: row.original.country_code,
            timeZone: row.original.iana,
          });
          return (
            <div className="text-left font-bold text-[9px] px-2 flex flex-col justify-between">
              <span className="leading-4">{date}</span>
              <span className="leading-2.5 text-[#555E68]">{time}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "end_date",
        id: "end_date",
        header: ({ column }) => {
          return (
            <Button
              className="text-left py-3 px-2 font-bold text-table-header"
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            >
              Date de fin
              <SortIcon column={column} />
            </Button>
          );
        },
        cell: ({ row }) => {
          const { date, time } = toLocalString({
            date: row.original.end_date,
            locale: row.original.country_code,
            timeZone: row.original.iana,
          });
          return (
            <div className="text-left font-bold text-[9px] px-2 flex flex-col justify-between">
              <span className="leading-4">{date}</span>
              <span className="leading-2.5 text-[#555E68]">{time}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "duration",
        id: "duration",
        header: () => (
          <div className="flex justify-center items-center">
            <span className="font-semibold text-table-header text-sm">Durée</span>
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-center font-black text-[9px] leading-4">
            {formatDuration(numberToDateTimeString(row.original.duration), {
              ...defaultDurationOptions,
              delimiter: " ",
              zero: false,
            })}
          </div>
        ),
      },
      {
        accessorKey: "license_plate",
        id: "license_plate",
        header: ({ column }) => {
          return (
            <div className="flex justify-center">
              <Button
                className="text-left py-3 px-2 font-semibold text-table-header"
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              >
                Immatriculation
                <SortIcon column={column} />
              </Button>
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="text-center font-black text-[9px] leading-4">{row.original.license_plate}</div>
        ),
      },
      {
        accessorKey: "amount",
        id: "amount",
        header: ({ column }) => {
          return (
            <div className="flex justify-center">
              <Button
                className="text-left py-3 px-2 font-semibold text-table-header"
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              >
                Montant
                <SortIcon column={column} />
              </Button>
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="text-center font-black text-[9px] leading-4">
            {row.original.amount ? moneyFormat(row.original.amount / 100) : "Gratuit"}
          </div>
        ),
      },
      {
        accessorKey: "payment_method",
        id: "payment_method",
        className: "text-center",
        header: () => (
          <div className="flex justify-center items-center">
            <span className="font-semibold text-table-header">Mode de paiement</span>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <PaymentMethodLogo
              paymentMethodWallet={row.original.payment_method.wallet}
              paymentMethodName={row.original.payment_method.brand}
            />
          </div>
        ),
      },
      {
        accessorKey: "user_profile",
        id: "user_profile",
        header: ({ column }) => {
          return (
            <div className="flex justify-center items-center gap-2">
              <Button
                className="text-left py-3 px-2 font-semibold text-table-header"
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              >
                Profil usager
                <SortIcon column={column} />
              </Button>
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="flex justify-center">
            <UserProfileBadge userProfile={row.original.user_profile} />
          </div>
        ),
      },
      {
        accessorKey: "zone_name",
        id: "zone_name",
        header: ({ column }) => {
          return (
            <div className="flex justify-center items-center gap-2">
              <Button
                className="text-left py-3 px-2 font-semibold text-table-header flex justify-center"
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              >
                Zone
                <SortIcon column={column} />
              </Button>
            </div>
          );
        },
        cell: ({ row }) => (
          <div className="flex justify-center">
            <ZoneNameBadge zoneName={row.original.zone_name} />
          </div>
        ),
      },
      {
        accessorKey: "state",
        id: "state",
        header: ({ column }) => {
          return (
            <div className="flex justify-center">
              <Button
                className="text-center py-3 px-2 font-semibold text-table-header"
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
              >
                Statut
                <SortIcon column={column} />
              </Button>
            </div>
          );
        },
        cell: ({ row }) => (
          <span className="flex justify-center">
            <StatusBadge status={row.original.state === "fulfilled"} />
          </span>
        ),
      },
      {
        accessorKey: "stopped",
        id: "stopped",
        header: () => {
          return (
            <div className="flex justify-center">
              <span className="font-semibold leading-2.5 text-[#555E68]">Arrêté / Prolongé</span>
            </div>
          );
        },
        cell: ({ row }) => (
          <span className="flex justify-center">
            <TransactionState stopped={row.original.stopped} extended={row.original.extended} />
          </span>
        ),
      },
      {
        accessorKey: "id",
        id: "id",
        header: () => <span className="text-center font-semibold text-table-header w-40">Transaction ID</span>,
        cell: ({ row }) => <span className="text-center font-bold text-[#2F333E] text-xs">{row.original.id}</span>,
      },
    ],
    [],
  );
};
